import React, { useEffect, useMemo } from "react";
import _ from "lodash";
import { PostHogFeature } from "posthog-js/react";
import { Button, Box } from "@showitinc/showit-wc";

import AppConstants from "../../constants/AppConstants";
import { isFormFieldElementType } from "../../constants/FormFieldConstants.mjs";
import UIActions from "../../actions/UIActions";
import DesignViewStore from "../../stores/DesignViewStore";
import Modal from "../base/Modal";
import DesignSettingsPanel from "./DesignSettingsPanel";
import UserActions from "../../actions/UserActions";
import DocumentStore from "../../document/DocumentStore";
import { COMMANDS } from "../../document/commands";
import DesignContextUtils from "../../utils/DesignContextUtils";
import DesignThemeStore from "../../stores/DesignThemeStore";
import useStoreState from "../providers/useStoreState";
import useImmutableState from "../../hooks/useImmutableState";
import uiPrefix from "../../utils/UiPrefixUtils";
import SiteDesignStore from "@/stores/SiteDesignStore";
import Immutable from "immutable";
import useV2Feature from "../../hooks/useV2Feature";
import { FORMS_FLAG } from "../../constants/FeatureFlags.mjs";

const {
  THEME_TAB_FONTS,
  THEME_TAB_STYLE,
  THEME_TAB_BUTTONS,
  THEME_TAB_BRAND_COLORS,
  THEME_TAB_FORM_FIELDS,
  BLOCK_PROP_PARENT_BLOCK_ID,
  BLOCK_PROP_PARENT_BLOCK,
} = AppConstants;

const DesignSettingsModal = (props) => {
  const { onHide, blockContainer, modalTitle, modalActionButton } = props;
  const tabView = useStoreState(DesignViewStore, DesignViewStore.getState);
  const themeChangeWatcher = useStoreState(
    DesignThemeStore,
    DesignThemeStore.getTheme
  );
  const [newTheme, setNewTheme] = useImmutableState(themeChangeWatcher);
  const [origTheme, setOrigTheme] = useImmutableState(themeChangeWatcher);

  useEffect(() => {
    setNewTheme(themeChangeWatcher);
    setOrigTheme(themeChangeWatcher);
  }, [themeChangeWatcher]);

  /**
   * Checks if a style entry is a button type
   */
  const isButtonStyle = (entry) => {
    return entry?.get?.("type") === "button";
  };

  const isFormStyle = (entry) => isFormFieldElementType(entry?.get?.("type"));

  /**
   * Resets specific style keys from original theme to new theme
   */
  const resetStyleKeys = (filterFn) => {
    const origStyles = origTheme.get("styles") ?? Immutable.Map();
    const currentStyles = newTheme.get("styles") ?? Immutable.Map();

    const nextStyles = currentStyles.withMutations((mutable) => {
      currentStyles.keySeq().forEach((styleKey) => {
        if (
          filterFn(styleKey, currentStyles.get(styleKey)) &&
          origStyles.has(styleKey)
        ) {
          mutable.set(styleKey, origStyles.get(styleKey));
        }
      });
    });

    return newTheme.set("styles", nextStyles);
  };

  /**
   * Resets theme settings - either for the active tab only or everything
   * @param {boolean} [resetAll=false] - If true, resets all settings; if false, resets only the active tab
   */
  const _resetTheme = (resetAll = false) => {
    let updatedTheme;

    if (resetAll) {
      // Reset everything
      updatedTheme = origTheme;
    } else {
      // Reset only the active tab
      switch (activeTab) {
        case THEME_TAB_BRAND_COLORS:
          updatedTheme = newTheme.set("colors", origTheme.get("colors"));
          break;

        case THEME_TAB_STYLE:
          // Reset all non-button styles
          updatedTheme = resetStyleKeys((_, entry) => !isButtonStyle(entry) && !isFormStyle(entry));
          // Reset theme bgcolor (Global Style) and sync state
          updatedTheme = updatedTheme
            .set("desktop", origTheme.get("desktop"))
            .set("mobile", origTheme.get("mobile"))
            .set("sync", origTheme.get("sync"));
          break;

        case THEME_TAB_FONTS:
          updatedTheme = newTheme.set("fonts", origTheme.get("fonts"));
          break;

        case THEME_TAB_BUTTONS:
          // Reset only button styles
          updatedTheme = resetStyleKeys((_, entry) => isButtonStyle(entry));
          break;

        case THEME_TAB_FORM_FIELDS:
          updatedTheme = resetStyleKeys((_, entry) => isFormStyle(entry));
          break;

        default:
          updatedTheme = origTheme;
      }
    }

    setNewTheme(updatedTheme);
    document.dispatchEvent(new CustomEvent("resetThemePanel"));
  };

  const _saveTheme = () => {
    DocumentStore.dispatch({ type: COMMANDS.UPDATE_THEME, payload: { theme: newTheme } });
    _hidePanel();
    UserActions.trackEvent({ event: "theme-save" });
  };

  // Create preview data similar to LayoutChooser
  const siteDesign = SiteDesignStore.getDesign();
  const currentPage = SiteDesignStore.getCurrentPage();

  // Make sure blocks have parent block data attached, so site canvas blocks can be rendered
  const blocks = (blockContainer.get("blocks") ?? Immutable.List()).map(
    (block) => {
      if (
        block.has(BLOCK_PROP_PARENT_BLOCK_ID) &&
        !block.has(BLOCK_PROP_PARENT_BLOCK)
      ) {
        return currentPage.convertToPageBlock(block.get("id"), siteDesign);
      }
      return block;
    }
  );

  const preview = {
    blocks,
    theme: newTheme,
    config: siteDesign.data.get("config", Immutable.Map()),
  };

  /**
   * Is called when the user clicks outside of the panel or the close button in the corner.
   */
  const _closePanel = () => {
    const isThemeModified = !newTheme.equals(origTheme);

    if (isThemeModified) {
      UIActions.modalOpen(AppConstants.MODAL_ACTION_CONFIRM, {
        title: "Save Design Settings",
        question: (
          <div>
            <div key="line1">
              You have made changes to your design settings.
            </div>
            <div key="line2">Do you want to save now?</div>
          </div>
        ),
        confirmBtn: "Save Now",
        confirmAction: _saveTheme,
        cancelBtn: "Close Without Saving",
        cancelAction: _closeWithoutSaving,
      });
    } else {
      _hidePanel();
    }
  };

  const _closeWithoutSaving = () => {
    _hidePanel();
    setTimeout(() => _resetTheme(true), 400);
  };

  const _hidePanel = () => {
    onHide && onHide();
  };

  const _toggleTabs = (tabName) => {
    UIActions.toggleActiveThemeSettingTab({ tabName });
  };

  const modalProps = {
    ..._.pick(props, Modal.BASIC_PROPS),
    enforceFocus: false,
  };
  const assetUrl = blockContainer.getIn(["config", "assetURL"]);
  // Stable, so previews memoised on it (font tags) don't redo their work.
  const designContext = useMemo(() => DesignContextUtils.createContext({ assetUrl }), [assetUrl]);
  const isFormFieldsTabEnabled = useV2Feature(FORMS_FLAG);
  const storedTab = tabView.getIn(["themePanel", "activeTab"]);
  // The store keeps Form Fields across design switches, so a v1 design falls back to the default tab.
  const activeTab =
    storedTab === THEME_TAB_FORM_FIELDS && !isFormFieldsTabEnabled
      ? THEME_TAB_BRAND_COLORS
      : storedTab;

  const resetLabelByTab = {
    [THEME_TAB_BRAND_COLORS]: "Brand Colors",
    [THEME_TAB_STYLE]: "Type Style",
    [THEME_TAB_FONTS]: "Fonts",
    [THEME_TAB_BUTTONS]: "Buttons",
    [THEME_TAB_FORM_FIELDS]: "Form Fields",
  };
  const resetLabel = `Reset ${resetLabelByTab[activeTab] || "Settings"}`;

  return (
    <Modal {...modalProps} onHide={_closePanel}>
      <Modal.Dialog size="large" className="theme-panel-modal">
        <Modal.Dialog.Header closeButton>
          <Modal.Dialog.Title>{modalTitle}</Modal.Dialog.Title>
          <div className="modal-header-filters">
            <div className="modal-header-tabset">
              <div
                data-e2e={uiPrefix(`site-style-tab`)}
                className={`${uiPrefix(`site-style-tab`)} modal-header-tab${
                  activeTab === THEME_TAB_BRAND_COLORS ? " active" : ""
                }`}
                onClick={() => _toggleTabs(THEME_TAB_BRAND_COLORS)}
              >
                Brand Colors
              </div>
              <div
                data-e2e={uiPrefix(`site-style-tab`)}
                className={`${uiPrefix(`site-style-tab`)} modal-header-tab${
                  activeTab === THEME_TAB_STYLE ? " active" : ""
                }`}
                onClick={() => _toggleTabs(THEME_TAB_STYLE)}
              >
                Type Styles
              </div>
              <div
                data-e2e={uiPrefix(`fonts-tab`)}
                className={`${uiPrefix(`fonts-tab`)} modal-header-tab${
                  activeTab === THEME_TAB_FONTS ? " active" : ""
                }`}
                onClick={() => _toggleTabs(THEME_TAB_FONTS)}
              >
                Fonts
              </div>
              {/* <PostHogFeature flag="buttons" match={true}> */}
              <div
                data-e2e={uiPrefix(`buttons-tab`)}
                className={`${uiPrefix(`buttons-tab`)} modal-header-tab${
                  activeTab === THEME_TAB_BUTTONS ? " active" : ""
                }`}
                onClick={() => _toggleTabs(THEME_TAB_BUTTONS)}
              >
                Buttons
              </div>
              {/* </PostHogFeature> */}
              {isFormFieldsTabEnabled && (
                <div
                  data-e2e={uiPrefix(`form-fields-tab`)}
                  className={`${uiPrefix(`form-fields-tab`)} modal-header-tab${
                    activeTab === THEME_TAB_FORM_FIELDS ? " active" : ""
                  }`}
                  onClick={() => _toggleTabs(THEME_TAB_FORM_FIELDS)}
                >
                  Form Fields
                </div>
              )}
            </div>
          </div>
        </Modal.Dialog.Header>
        <Modal.Dialog.Body style={{ padding: 0, display: "flex" }}>
          <DesignSettingsPanel
            preview={preview}
            activeTab={activeTab}
            designContext={designContext}
            newTheme={newTheme}
            setNewTheme={setNewTheme}
          />
        </Modal.Dialog.Body>
        <Modal.Dialog.Footer>
          <Box d="flex" justify="between">
            <Box>
              <Button
                variant="minimal"
                size="lg"
                data-e2e={uiPrefix("modal-reset-btn")}
                className={uiPrefix("modal-reset-btn")}
                onClick={() => _resetTheme()}
              >
                {resetLabel}
              </Button>
            </Box>
            <Box d="flex" gp="2">
              <Button
                variant="minimal"
                size="lg"
                data-e2e={uiPrefix("modal-cancel-btn")}
                className={uiPrefix("modal-cancel-btn")}
                onClick={_closePanel}
              >
                Cancel
              </Button>
              <Button
                variant="feature"
                size="lg"
                data-e2e={uiPrefix("modal-save-btn")}
                className={uiPrefix("modal-save-btn")}
                onClick={_saveTheme}
              >
                {modalActionButton}
              </Button>
            </Box>
          </Box>
        </Modal.Dialog.Footer>
      </Modal.Dialog>
    </Modal>
  );
};

export default DesignSettingsModal;
